import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { deleteShift, listUpcomingShifts } from "@/features/ops/api/opsApi";
import { useOpsStore } from "@/features/ops/model/opsStore";
import type { ShiftStatus, UpcomingShiftRow } from "@/features/ops/types";
import { CATEGORY_LABELS, ROLE_LABELS, type ShiftCategory } from "@/features/shifts/types";
import { cn } from "@/shared/lib/cn";
import { formatTimeRange12h } from "@/shared/lib/time";
import { Badge, Button, ConfirmDialog } from "@/shared/ui";

type StatusFilter = "all" | "OPEN" | "FILLED";

const FILTERS: Array<{ label: string; value: StatusFilter }> = [
  { label: "All", value: "all" },
  { label: "Needs worker", value: "OPEN" },
  { label: "Confirmed", value: "FILLED" },
];

function shiftHours(start: string, end: string): number {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  return mins / 60;
}

function fmtDate(iso: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso + "T00:00:00");
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  return target.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function StatusBadge({ status }: { status: ShiftStatus }) {
  if (status === "OPEN") {
    return (
      <Badge variant="warning" dot>
        Needs worker
      </Badge>
    );
  }
  if (status === "FILLED") {
    return (
      <Badge variant="success" dot>
        Confirmed
      </Badge>
    );
  }
  return <Badge variant="neutral">{status}</Badge>;
}

export function OpsDashboard() {
  const token = useOpsStore((s) => s.token)!;
  const qc = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [confirmDelete, setConfirmDelete] = useState<UpcomingShiftRow | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops", "upcoming-shifts"],
    queryFn: () => listUpcomingShifts(token),
    // Live operational view — venue/worker actions in other sessions can't
    // reach our cache, so poll while the dashboard is foregrounded.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const remove = useMutation({
    mutationFn: (shiftId: string) => deleteShift(token, shiftId),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["ops", "upcoming-shifts"] });
      qc.invalidateQueries({ queryKey: ["ops", "payouts"] });
    },
  });

  const filtered = useMemo(() => {
    if (!data) return [] as UpcomingShiftRow[];
    if (filter === "all") return data;
    return data.filter((r) => r.status === filter);
  }, [data, filter]);

  const counts = useMemo(() => {
    const all = data ?? [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayIso = today.toISOString().slice(0, 10);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const week = new Date(today);
    week.setDate(week.getDate() + 7);
    const openShifts = all.filter((r) => r.status === "OPEN");
    const pendingApplicants = openShifts.reduce(
      (sum, r) => sum + (r.pending_applicant_count ?? 0),
      0,
    );
    const shiftsWithNoApplicants = openShifts.filter(
      (r) => (r.pending_applicant_count ?? 0) === 0,
    ).length;
    return {
      total: all.length,
      open: openShifts.length,
      filled: all.filter((r) => r.status === "FILLED").length,
      today: all.filter((r) => r.date === todayIso).length,
      next7: all.filter((r) => {
        const d = new Date(r.date + "T00:00:00");
        return d >= today && d < week;
      }).length,
      pendingApplicants,
      shiftsWithNoApplicants,
    };
  }, [data]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-display-tight text-copy">Dashboard</h2>
        <p className="text-xs text-copy-muted">
          Upcoming shifts at a glance. Status reflects whether a worker is confirmed yet.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <SummaryCard label="Upcoming total" value={counts.total} />
        <SummaryCard label="Today" value={counts.today} />
        <SummaryCard label="Needs worker" value={counts.open} tone="warning" />
        <SummaryCard label="Next 7 days" value={counts.next7} />
        <SummaryCard
          label="Pending applicants"
          value={counts.pendingApplicants}
          caption={`across ${counts.open} open shift${counts.open === 1 ? "" : "s"}`}
        />
        <SummaryCard
          label="0 applicants"
          value={counts.shiftsWithNoApplicants}
          tone={counts.shiftsWithNoApplicants > 0 ? "warning" : undefined}
          caption="open shifts without any applicants"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1.5 rounded-md border border-border bg-surface-sunken p-0.5">
          {FILTERS.map((t) => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              className={cn(
                "whitespace-nowrap rounded-[5px] px-2.5 py-1 text-xs font-medium transition-all",
                filter === t.value
                  ? "bg-surface text-copy shadow-card"
                  : "text-copy-muted hover:text-copy",
              )}
            >
              {t.label}
              {t.value !== "all" && (
                <span className="ml-1.5 tabular-nums text-copy-subtle">
                  {t.value === "OPEN" ? counts.open : counts.filled}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div className="text-sm text-copy-muted">Loading…</div>}
      {error && <div className="text-sm text-destructive">Could not load upcoming shifts.</div>}

      {data && filtered.length === 0 && (
        <div className="rounded-lg border border-border bg-surface p-10 text-center text-sm text-copy-muted shadow-card">
          {filter === "OPEN"
            ? "No shifts currently need a worker."
            : filter === "FILLED"
              ? "No confirmed shifts coming up."
              : "No upcoming shifts."}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-card">
         <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-sm">
            <thead className="border-b border-border bg-surface-sunken text-left text-xs uppercase tracking-wider text-copy-muted">
              <tr>
                <th className="px-4 py-3 font-medium">When</th>
                <th className="px-4 py-3 font-medium">Venue</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Spots</th>
                <th className="px-4 py-3 font-medium">Worker</th>
                <th className="px-4 py-3 text-right font-medium">Pay</th>
                <th className="px-4 py-3 text-right font-medium">Applicants</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((row) => {
                const hrs = shiftHours(row.start_time, row.end_time);
                return (
                  <tr
                    key={row.shift_id}
                    className="transition-colors hover:bg-surface-sunken/40"
                  >
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-copy">{fmtDate(row.date)}</div>
                      <div className="text-xs text-copy-muted">
                        {formatTimeRange12h(row.start_time, row.end_time)} · {hrs}h
                      </div>
                      {row.end_date && row.end_date !== row.date && (
                        <div className="text-xs text-copy-subtle">
                          thru {new Date(row.end_date + "T00:00:00").toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-copy">{row.venue_name}</div>
                      {row.category && (
                        <div className="text-xs text-copy-muted">
                          {CATEGORY_LABELS[row.category as ShiftCategory] ?? row.category}
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap gap-1">
                        {row.is_urgent && (
                          <Badge variant="destructive">Urgent</Badge>
                        )}
                        {row.transportation_offered && (
                          <Badge variant="neutral">Transit</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-copy">
                      <div>{ROLE_LABELS[row.role]}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {row.posting_slots_total > 1 ? (
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
                            row.posting_slots_filled >= row.posting_slots_total
                              ? "bg-success-light text-success-foreground"
                              : row.posting_slots_filled > 0
                                ? "bg-warning-light text-warning-foreground"
                                : "bg-surface-sunken text-copy-muted",
                          )}
                          title={`${row.posting_slots_filled} of ${row.posting_slots_total} slots in this posting are filled`}
                        >
                          {row.posting_slots_filled}/{row.posting_slots_total}
                        </span>
                      ) : (
                        <span className="text-xs text-copy-subtle">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {row.worker_name ? (
                        <div>
                          <div className="font-medium text-copy">{row.worker_name}</div>
                          <div className="text-xs text-copy-muted">
                            {row.worker_phone ?? row.worker_email}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-copy-subtle">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-right font-semibold tabular-nums text-copy">
                      ${row.pay_rate}/h
                    </td>
                    <td className="px-4 py-3 align-top text-right tabular-nums">
                      {row.status === "OPEN" ? (
                        row.pending_applicant_count > 0 ? (
                          <span className="font-medium text-copy">
                            {row.pending_applicant_count}
                          </span>
                        ) : (
                          <span className="text-warning-foreground">0</span>
                        )
                      ) : (
                        <span className="text-copy-subtle">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <Button
                        variant="outline"
                        size="icon-sm"
                        aria-label="Delete shift"
                        onClick={() => setConfirmDelete(row)}
                        className="text-destructive-foreground hover:bg-destructive-light"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
         </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        destructive
        title="Delete this shift?"
        description={
          <>
            This permanently deletes the {confirmDelete && ROLE_LABELS[confirmDelete.role]}{" "}
            shift at{" "}
            <span className="font-medium text-copy">{confirmDelete?.venue_name}</span>{" "}
            and its applications. Use this to clear test shifts. This cannot be
            undone.
          </>
        }
        confirmLabel="Delete permanently"
        loadingLabel="Deleting…"
        loading={remove.isPending}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.shift_id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
  caption,
}: {
  label: string;
  value: number;
  tone?: "warning";
  caption?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3 shadow-card">
      <div className="text-xs uppercase tracking-wider text-copy-muted">{label}</div>
      <div
        className={cn(
          "mt-1 text-2xl font-semibold tabular-nums tracking-display-tight",
          tone === "warning" && value > 0 ? "text-warning-foreground" : "text-copy",
        )}
      >
        {value}
      </div>
      {caption && <div className="mt-0.5 text-xs text-copy-subtle">{caption}</div>}
    </div>
  );
}
